import React, { useState } from "react";
import { TOKENS } from "../tokens";
import { Card } from "../components/Card";
import { SectionLabel } from "../components/SectionLabel";
import { StatusPill } from "../components/StatusPill";
import { useApp } from "../context/AppContext";
import { getImageUrl } from "../api/client";
import { is18Plus, validatePhone, validateEmail } from "../utils/validation";
import { MapPin, ChevronRight, ChevronLeft, Search, FileText, Clock, Calendar, UserCheck, UserX, Navigation, ExternalLink } from "lucide-react";
import { getEmpStatus, isEmpAbsent } from "../utils/empStatus";
import { fmtDate } from "../hooks/useClock";

// ── Attendance calendar helpers ───────────────────────────────────────────────
const ATT_COLORS = {
  present: TOKENS.gold,
  od:      TOKENS.blue,
  late:    TOKENS.warning,
  absent:  TOKENS.danger,
  off:     "#CCC",
};
const ATT_LABELS = {
  present: "Present", od: "On OD", late: "Late", absent: "Absent", off: "Off",
};
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const FILTERS = ["All", "Checked In", "On OD", "Daily Report", "Checked Out", "Absent"];

export function TeamScreen({ onSelectEmp }) {
  const { team, assignTask, addEmployee } = useApp();
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  // ── View mode: "team" | "attendance" ─────────────────────────────────────
  const [viewMode, setViewMode] = useState("team");

  // ── Attendance calendar state ─────────────────────────────────────────────
  const getTodayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [attEmpId, setAttEmpId] = useState(null);
  const [attYear, setAttYear]   = useState(new Date().getFullYear());
  const [attMonth, setAttMonth] = useState(new Date().getMonth());
  const [attDate, setAttDate]   = useState(getTodayStr());

  const attEmp = attEmpId ? team.find(e => e.id === attEmpId) : null;

  const attPrevMonth = () => { if (attMonth === 0) { setAttMonth(11); setAttYear(y => y-1); } else setAttMonth(m => m-1); };
  const attNextMonth = () => { if (attMonth === 11) { setAttMonth(0); setAttYear(y => y+1); } else setAttMonth(m => m+1); };

  // Assign task modal states
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [selectedEmpId, setSelectedEmpId] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskCategory, setTaskCategory] = useState("Calibration");
  const [taskLocation, setTaskLocation] = useState("");
  const [clientRef, setClientRef] = useState("");
  const [taskClientName, setTaskClientName] = useState("");
  const [taskProjectName, setTaskProjectName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Add Employee modal states
  const [isAddEmpOpen, setIsAddEmpOpen] = useState(false);
  const [newEmpId, setNewEmpId] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("");
  const [newFatherName, setNewFatherName] = useState("");
  const [newMotherName, setNewMotherName] = useState("");
  const [newDob, setNewDob] = useState("");
  const [newBloodGroup, setNewBloodGroup] = useState("B+");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newEmergencyName, setNewEmergencyName] = useState("");
  const [newEmergencyRelationship, setNewEmergencyRelationship] = useState("Father");
  const [newEmergencyPhone, setNewEmergencyPhone] = useState("");
  const [newClient, setNewClient] = useState("");
  const [newTeam, setNewTeam] = useState("");
  const [newJoiningDate, setNewJoiningDate] = useState(new Date().toISOString().slice(0, 10));
  const [newPassword, setNewPassword] = useState("");
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [errorAdd, setErrorAdd] = useState("");

  const handleOpenAddEmpModal = () => {
    setIsAddEmpOpen(true);
    setNewEmpId("");
    setNewName("");
    setNewRole("");
    setNewFatherName("");
    setNewMotherName("");
    setNewDob("");
    setNewBloodGroup("B+");
    setNewPhone("");
    setNewEmail("");
    setNewEmergencyName("");
    setNewEmergencyRelationship("Father");
    setNewEmergencyPhone("");
    setNewClient("");
    setNewTeam("");
    setNewJoiningDate(new Date().toISOString().slice(0, 10));
    setNewPassword("");
    setErrorAdd("");
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!newEmpId.trim() || !newName.trim() || !newRole.trim() || !newPassword.trim()) {
      setErrorAdd("Employee ID, Name, Role, and Password are required.");
      return;
    }
    if (newDob && !is18Plus(newDob)) {
      setErrorAdd("Date of Birth must indicate age 18 or older.");
      return;
    }
    if (newPhone && !validatePhone(newPhone)) {
      setErrorAdd("Phone number must be exactly 10 digits.");
      return;
    }
    if (newEmail && !validateEmail(newEmail)) {
      setErrorAdd("Email address must be valid and end with .com (e.g. name@gmail.com).");
      return;
    }

    setErrorAdd("");
    setLoadingAdd(true);
    try {
      await addEmployee({
        employeeId: newEmpId.trim(),
        name: newName.trim(),
        role: newRole.trim(),
        fatherName: newFatherName.trim(),
        motherName: newMotherName.trim(),
        dob: newDob,
        bloodGroup: newBloodGroup,
        phone: newPhone.trim(),
        email: newEmail.trim(),
        emergencyName: newEmergencyName.trim(),
        emergencyRelationship: newEmergencyRelationship.trim(),
        emergencyPhone: newEmergencyPhone.trim(),
        clientName: newClient.trim(),
        teamName: newTeam.trim(),
        joiningDate: newJoiningDate,
        password: newPassword.trim(),
      });
      setIsAddEmpOpen(false);
    } catch (err) {
      setErrorAdd(err.response?.data?.error || "Failed to add employee.");
    } finally {
      setLoadingAdd(false);
    }
  };

  const handleOpenAssignModal = () => {
    setIsAssignOpen(true);
    setSelectedEmpId(team[0]?.employee_id || "");
    setTaskTitle("");
    setTaskCategory("Calibration");
    setTaskLocation("");
    setTaskClientName("");
    setTaskProjectName("");
    setClientRef(`REF-${Date.now().toString().slice(-4)}`);
    setErrorMsg("");
  };

  const handleAssignTask = async (e) => {
    e.preventDefault();
    if (!taskTitle.trim() || !taskLocation.trim()) {
      setErrorMsg("Please fill in both Task Title and Location.");
      return;
    }
    setErrorMsg("");
    setLoading(true);
    try {
      await assignTask({
        employeeId: selectedEmpId,
        title: taskTitle.trim(),
        category: taskCategory,
        location: taskLocation.trim(),
        clientRef: clientRef.trim(),
        clientName: taskClientName.trim(),
        projectName: taskProjectName.trim(),
      });
      setIsAssignOpen(false);
    } catch (err) {
      setErrorMsg("Failed to assign task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const filtered = team.filter((emp) => {
    const matchSearch = emp.name.toLowerCase().includes(search.toLowerCase()) ||
      (emp.lastLocation || "").toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (filter === "All") return true;
    if (filter === "Checked In") return emp.checkedIn && !emp.onOD;
    if (filter === "On OD") return emp.onOD;
    if (filter === "Daily Report") return emp.hasSubmittedReportToday || (emp.reports && emp.reports.some(r => r.date === today));
    if (filter === "Checked Out") return !emp.checkedIn && !emp.onOD && !!emp.checkOutTime;
    if (filter === "Absent") return isEmpAbsent(emp);
    return true;
  }).sort((a, b) => b.score - a.score);

  if (viewMode === "attendance" && attEmp) {
    const attendance = attEmp.attendance || {};
    const checkinsHistory = attEmp.checkinsHistory || [];
    const selectedCheckins = checkinsHistory.filter(c => c.date === attDate).sort((a,b) => a.id - b.id);
    const checkInRecord = selectedCheckins.find(c => c.type === 'in');
    const checkOutRecord = selectedCheckins.slice().reverse().find(c => c.type === 'out');
    const selectedStatus = attendance[attDate] || (attDate > getTodayStr() ? "off" : "absent");
    const daysInMonth = new Date(attYear, attMonth + 1, 0).getDate();
    const firstDayOfWeek = new Date(attYear, attMonth, 1).getDay();

    const legend = [
      { label: "Present", color: TOKENS.gold },
      { label: "On OD",   color: TOKENS.blue },
      { label: "Late",    color: TOKENS.warning },
      { label: "Absent",  color: TOKENS.danger },
    ];

    return (
      <div>
        <button onClick={() => setAttEmpId(null)} style={{
          display: "flex", alignItems: "center", gap: 6, border: "none",
          background: "none", color: TOKENS.navyDeep, cursor: "pointer",
          marginBottom: 14, fontWeight: 700, fontSize: 13,
        }}>
          ← Back to All Agents
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 44, height: 44, borderRadius: "50%", background: TOKENS.navyDeep,
            color: TOKENS.gold, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18, fontWeight: 700
          }}>
            {attEmp.initials || "EM"}
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: TOKENS.navyDeep }}>{attEmp.name}</h2>
            <div style={{ fontSize: 12, color: TOKENS.muted }}>{attEmp.role}</div>
          </div>
        </div>

        {/* Legend */}
        <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          {legend.map(({ label, color }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
              <span style={{ fontSize: 11, color: TOKENS.muted, fontWeight: 600 }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Calendar Section */}
        <Card style={{ padding: "14px 12px", marginBottom: 24, overflowX: "auto" }}>
          {/* Month Selector Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep }}>
                {MONTH_NAMES[attMonth]} {attYear}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button onClick={attPrevMonth} style={{
                background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
                borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center"
              }}>
                <ChevronLeft size={18} color={TOKENS.navyDeep} />
              </button>
              <button onClick={() => {
                const now = new Date();
                setAttYear(now.getFullYear());
                setAttMonth(now.getMonth());
                setAttDate(getTodayStr());
              }} style={{
                background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
                borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: TOKENS.navyDeep, cursor: "pointer"
              }}>
                Today
              </button>
              <button onClick={attNextMonth} style={{
                background: TOKENS.cream, border: `1px solid ${TOKENS.border}`,
                borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center"
              }}>
                <ChevronRight size={18} color={TOKENS.navyDeep} />
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4, width: "100%" }}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
              <div key={day} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: TOKENS.muted, padding: "4px 0" }}>{day}</div>
            ))}
            
            {Array.from({ length: firstDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} style={{ minHeight: 44, background: "transparent" }} />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const dayNum = i + 1;
              const dateStr = `${attYear}-${String(attMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
              const isSelected = dateStr === attDate;
              const isToday = dateStr === getTodayStr();
              const isFuture = dateStr > getTodayStr();
              
              const status = isFuture ? "off" : (attendance[dateStr] || "absent");

              return (
                <div
                  key={dateStr}
                  onClick={() => !isFuture && setAttDate(dateStr)}
                  style={{
                    minHeight: 46, borderRadius: 8, padding: "4px 4px", cursor: isFuture ? "not-allowed" : "pointer",
                    display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "center",
                    border: isSelected ? `2px solid ${TOKENS.navyDeep}` : isToday ? `1.5px solid ${TOKENS.gold}` : `1px solid ${TOKENS.border}`,
                    background: isSelected ? `${TOKENS.navyDeep}10` : isToday ? "rgba(10,25,50,0.03)" : "#fff",
                    transition: "all 0.15s ease", opacity: isFuture ? 0.5 : 1
                  }}
                >
                  <span style={{ fontSize: 11.5, fontWeight: isSelected || isToday ? 800 : 600, color: isSelected ? TOKENS.navyDeep : TOKENS.ink }}>
                    {dayNum}
                  </span>
                  {!isFuture && (
                    <div style={{
                      width: 12, height: 12, borderRadius: "50%",
                      background: ATT_COLORS[status] || "#EEE",
                      marginTop: 4
                    }} title={ATT_LABELS[status]} />
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Selected Date Details */}
        <SectionLabel>Details for {attDate}</SectionLabel>
        <Card style={{ padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <div style={{ padding: "4px 10px", borderRadius: 8, fontSize: 12, fontWeight: 700, color: "#fff", background: ATT_COLORS[selectedStatus] || "#999" }}>
              {ATT_LABELS[selectedStatus]}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, background: TOKENS.cream, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${TOKENS.success}20`, color: TOKENS.success, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4, flexShrink: 0 }}>
                <Clock size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: TOKENS.muted, fontWeight: 700, textTransform: "uppercase" }}>Check In</div>
                {checkInRecord ? (
                  <>
                    <div style={{ fontSize: 15, fontWeight: 800, color: TOKENS.navyDeep, margin: "2px 0" }}>
                      {checkInRecord.timestamp} · <span style={{ fontSize: 13, fontWeight: 600, color: TOKENS.navySoft }}>{fmtDate(checkInRecord.date)}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: TOKENS.navySoft, marginTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <MapPin size={12} color={TOKENS.navySoft} />
                        <span><b>Location:</b> {checkInRecord.city || "Unknown Location"}</span>
                      </div>
                      {checkInRecord.lat && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Navigation size={12} color={TOKENS.navySoft} />
                          <span>
                            <b>GPS Coords:</b> {checkInRecord.lat.toFixed(6)}° N, {checkInRecord.lng.toFixed(6)}° E 
                            {checkInRecord.accuracy ? ` (±${checkInRecord.accuracy}m)` : ""} · 
                            <span style={{ color: checkInRecord.isRealGps ? TOKENS.success : TOKENS.warning, fontWeight: 700 }}>
                              {checkInRecord.isRealGps ? " Live GPS" : " Network"}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    {checkInRecord.lat && (
                      <a
                        href={`https://www.google.com/maps?q=${checkInRecord.lat},${checkInRecord.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8,
                          fontSize: 11, fontWeight: 700, color: TOKENS.blue, textDecoration: "none",
                          background: `${TOKENS.blue}15`, padding: "4px 8px", borderRadius: 6,
                        }}
                      >
                        <ExternalLink size={12} /> View Map
                      </a>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 14, color: TOKENS.muted, fontStyle: "italic", marginTop: 2 }}>Not checked in on this date</div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "flex-start", gap: 12, background: TOKENS.cream, padding: 12, borderRadius: 10, border: `1px solid ${TOKENS.border}` }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${TOKENS.danger}20`, color: TOKENS.danger, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4, flexShrink: 0 }}>
                <Clock size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: TOKENS.muted, fontWeight: 700, textTransform: "uppercase" }}>Check Out</div>
                {checkOutRecord ? (
                  <>
                    <div style={{ fontSize: 15, fontWeight: 800, color: TOKENS.navyDeep, margin: "2px 0" }}>
                      {checkOutRecord.timestamp} · <span style={{ fontSize: 13, fontWeight: 600, color: TOKENS.navySoft }}>{fmtDate(checkOutRecord.date)}</span>
                    </div>
                    <div style={{ fontSize: 11.5, color: TOKENS.navySoft, marginTop: 4, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <MapPin size={12} color={TOKENS.navySoft} />
                        <span><b>Location:</b> {checkOutRecord.city || "Unknown Location"}</span>
                      </div>
                      {checkOutRecord.lat && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <Navigation size={12} color={TOKENS.navySoft} />
                          <span>
                            <b>GPS Coords:</b> {checkOutRecord.lat.toFixed(6)}° N, {checkOutRecord.lng.toFixed(6)}° E 
                            {checkOutRecord.accuracy ? ` (±${checkOutRecord.accuracy}m)` : ""} · 
                            <span style={{ color: checkOutRecord.isRealGps ? TOKENS.success : TOKENS.warning, fontWeight: 700 }}>
                              {checkOutRecord.isRealGps ? " Live GPS" : " Network"}
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                    {checkOutRecord.lat && (
                      <a
                        href={`https://www.google.com/maps?q=${checkOutRecord.lat},${checkOutRecord.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8,
                          fontSize: 11, fontWeight: 700, color: TOKENS.blue, textDecoration: "none",
                          background: `${TOKENS.blue}15`, padding: "4px 8px", borderRadius: 6,
                        }}
                      >
                        <ExternalLink size={12} /> View Map
                      </a>
                    )}
                  </>
                ) : (
                  <div style={{ fontSize: 14, color: TOKENS.muted, fontStyle: "italic", marginTop: 2 }}>Not checked out on this date</div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {/* View Toggle */}
      <div style={{ display: "flex", background: `${TOKENS.border}44`, borderRadius: 12, padding: 3, marginBottom: 18, maxWidth: 300, border: `1.5px solid ${TOKENS.border}` }}>
        <button
          onClick={() => { setViewMode("team"); setAttEmpId(null); }}
          style={{
            flex: 1, padding: "8px 12px", border: "none", borderRadius: 10,
            fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.15s ease",
            background: viewMode === "team" ? TOKENS.navyDeep : "transparent",
            color: viewMode === "team" ? "#fff" : TOKENS.muted,
          }}
        >
          Team View
        </button>
        <button
          onClick={() => { setViewMode("attendance"); setAttEmpId(null); }}
          style={{
            flex: 1, padding: "8px 12px", border: "none", borderRadius: 10,
            fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "all 0.15s ease",
            background: viewMode === "attendance" ? TOKENS.navyDeep : "transparent",
            color: viewMode === "attendance" ? "#fff" : TOKENS.muted,
          }}
        >
          Attendance
        </button>
      </div>

      {/* Header with Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
        <h2 style={{ fontFamily: "Fraunces, serif", fontSize: 24, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
          Regional Employees
        </h2>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={handleOpenAddEmpModal}
            style={{
              background: "#fff", color: TOKENS.navyDeep, border: `1.5px solid ${TOKENS.navyDeep}`,
              borderRadius: 12, padding: "9px 16px", fontWeight: 700,
              fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
            }}
          >
            ✦ Add Employee
          </button>
          <button
            onClick={handleOpenAssignModal}
            style={{
              background: TOKENS.navyDeep, color: "#fff", border: "none",
              borderRadius: 12, padding: "10px 18px", fontWeight: 700,
              fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
              boxShadow: `0 4px 14px ${TOKENS.navyDeep}22`
            }}
          >
            ✦ Assign New Task
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <Search size={15} color={TOKENS.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input
          placeholder="Search by name or city…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%", padding: "12px 12px 12px 38px", borderRadius: 12,
            border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5, outline: "none",
            background: "#fff", color: TOKENS.ink,
          }}
        />
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", paddingBottom: 2 }}>
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: "6px 14px", borderRadius: 20,
            background: filter === f ? TOKENS.navyDeep : "#fff",
            color: filter === f ? "#fff" : TOKENS.muted,
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            border: `1.5px solid ${filter === f ? TOKENS.navyDeep : TOKENS.border}`,
            whiteSpace: "nowrap",
          }}>{f}</button>
        ))}
      </div>

      {/* Leaderboard */}
      <SectionLabel right={<span style={{ fontSize: 11, color: TOKENS.muted }}>{filtered.length} agents</span>}>
        Team Leaderboard — Score Rank
      </SectionLabel>

      {filtered.length === 0 ? (
        <Card style={{ padding: 32, textAlign: "center", background: "#FAFBFD", margin: "10px 0" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: TOKENS.navyDeep, marginBottom: 4 }}>
            No Employees in System
          </div>
          <div style={{ fontSize: 12, color: TOKENS.muted, marginBottom: 16 }}>
            Click "+ Add Employee" above to manually add your field agents one by one.
          </div>
          <button
            onClick={handleOpenAddEmpModal}
            style={{
              background: TOKENS.navyDeep, color: "#fff", border: "none",
              borderRadius: 10, padding: "9px 18px", fontWeight: 700,
              fontSize: 12.5, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            ✦ Add Employee Now
          </button>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((emp, rank) => (
            <div key={emp.id} className="stagger-item" style={{ animationDelay: `${rank * 0.04}s` }}>
              <Card onClick={() => {
                if (viewMode === "attendance") {
                  setAttEmpId(emp.id);
                } else {
                  onSelectEmp(emp.id);
                }
              }} style={{ padding: 14, cursor: "pointer" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {/* Rank */}
                  <div style={{
                    width: 24, textAlign: "center",
                    fontFamily: "Fraunces, serif", fontWeight: 700,
                    fontSize: 14, color: rank === 0 ? TOKENS.gold : TOKENS.muted,
                    flexShrink: 0,
                  }}>
                    {rank + 1}
                  </div>

                  {/* Avatar */}
                  <div style={{
                    width: 42, height: 42, borderRadius: "50%",
                    background: emp.checkedIn ? TOKENS.navyMid : "#CCC",
                    color: "#fff", display: "flex", alignItems: "center",
                    justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0,
                    border: `2px solid ${emp.onOD ? TOKENS.blue : emp.checkedIn ? TOKENS.success : TOKENS.border}`,
                  }}>
                    {emp.selfie
                      ? <img src={getImageUrl(emp.selfie)} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                      : emp.initials
                    }
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: TOKENS.ink }}>{emp.name}</div>
                      <div style={{ fontFamily: "Fraunces, serif", fontSize: 16, fontWeight: 700, color: TOKENS.navyDeep }}>{emp.score}</div>
                    </div>
                    <div style={{ fontSize: 11.5, color: TOKENS.muted, marginTop: 2 }}>{emp.role}</div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <MapPin size={11} color={TOKENS.muted} />
                        <span style={{ fontSize: 11, color: TOKENS.muted }}>{emp.lastLocation}</span>
                        {(emp.checkInLocation?.lat || emp.check_in_lat) && (
                          <a
                            href={`https://www.google.com/maps?q=${emp.checkInLocation?.lat || emp.check_in_lat},${emp.checkInLocation?.lng || emp.check_in_lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              fontSize: 10, fontWeight: 700, color: TOKENS.blue, textDecoration: "none",
                              background: `${TOKENS.blue}15`, padding: "2px 6px", borderRadius: 6,
                              display: "inline-flex", alignItems: "center", gap: 2
                            }}
                          >
                            🗺️ Map
                          </a>
                        )}
                      </div>
                      {(() => {
                        const { status, checkOutTime } = getEmpStatus(emp);
                        return <StatusPill status={status} checkOutTime={checkOutTime} />;
                      })()}
                    </div>

                    {viewMode === "attendance" ? (
                      (() => {
                        const todayCheckins = (emp.checkinsHistory || []).filter(c => c.date === today);
                        const checkInRec = todayCheckins.find(c => c.type === "in");
                        const checkOutRec = [...todayCheckins].reverse().find(c => c.type === "out");

                        return (
                          <div style={{ 
                            marginTop: 10, 
                            display: "flex", 
                            flexDirection: "column", 
                            gap: 8, 
                            background: "#F8FAFC", 
                            borderRadius: 10, 
                            padding: 10,
                            border: `1px solid ${TOKENS.border}`
                          }}>
                            {/* Check In Row */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 6 }}>
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: TOKENS.success }}>📥 CHECK IN DETAILS</span>
                                {checkInRec ? (
                                  <>
                                    <span style={{ fontSize: 11.5, fontWeight: 600, color: TOKENS.ink, marginTop: 2 }}>
                                      📅 {fmtDate(checkInRec.date)} at {checkInRec.timestamp}
                                    </span>
                                    {checkInRec.lat && (
                                      <span style={{ fontSize: 10.5, color: TOKENS.muted, marginTop: 1 }}>
                                        🛰️ {checkInRec.lat.toFixed(6)}°, {checkInRec.lng.toFixed(6)}° (±{checkInRec.accuracy || 0}m) · {checkInRec.isRealGps ? "Live GPS" : "Network"}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ fontSize: 11.5, color: TOKENS.muted, fontStyle: "italic", marginTop: 2 }}>— No check-in recorded today —</span>
                                )}
                              </div>
                              {checkInRec?.lat && (
                                <a
                                  href={`https://www.google.com/maps?q=${checkInRec.lat},${checkInRec.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    fontSize: 10, fontWeight: 700, color: TOKENS.blue, textDecoration: "none",
                                    background: `${TOKENS.blue}15`, padding: "2px 6px", borderRadius: 6,
                                    alignSelf: "flex-start",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 2
                                  }}
                                >
                                  🗺️ Map
                                </a>
                              )}
                            </div>

                            {/* Check Out Row */}
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 6, borderTop: `1px dashed ${TOKENS.border}`, paddingTop: 6 }}>
                              <div style={{ display: "flex", flexDirection: "column" }}>
                                <span style={{ fontSize: 11, fontWeight: 700, color: TOKENS.danger }}>🚪 CHECK OUT DETAILS</span>
                                {checkOutRec ? (
                                  <>
                                    <span style={{ fontSize: 11.5, fontWeight: 600, color: TOKENS.ink, marginTop: 2 }}>
                                      📅 {fmtDate(checkOutRec.date)} at {checkOutRec.timestamp}
                                    </span>
                                    {checkOutRec.lat && (
                                      <span style={{ fontSize: 10.5, color: TOKENS.muted, marginTop: 1 }}>
                                        🛰️ {checkOutRec.lat.toFixed(6)}°, {checkOutRec.lng.toFixed(6)}° (±{checkOutRec.accuracy || 0}m) · {checkOutRec.isRealGps ? "Live GPS" : "Network"}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ fontSize: 11.5, color: TOKENS.muted, fontStyle: "italic", marginTop: 2 }}>
                                    {emp.checkedIn ? "🚪 Duty In Progress (Checked In)" : "— No check-out recorded —"}
                                  </span>
                                )}
                              </div>
                              {checkOutRec?.lat && (
                                <a
                                  href={`https://www.google.com/maps?q=${checkOutRec.lat},${checkOutRec.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    fontSize: 10, fontWeight: 700, color: TOKENS.blue, textDecoration: "none",
                                    background: `${TOKENS.blue}15`, padding: "2px 6px", borderRadius: 6,
                                    alignSelf: "flex-start",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 2
                                  }}
                                >
                                  🗺️ Map
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                        <span style={{ fontSize: 11, color: TOKENS.muted }}>
                          Tasks: <strong style={{ color: TOKENS.ink }}>{emp.tasksToday?.done || 0}/{emp.tasksToday?.total || 0}</strong>
                        </span>
                        <span style={{ fontSize: 11, color: TOKENS.muted }}>
                          Streak: <strong style={{ color: TOKENS.ink }}>{emp.streak || 0}d</strong>
                        </span>
                        <span style={{ fontSize: 11, color: TOKENS.muted }}>
                          {emp.lastSeen}
                        </span>
                      </div>
                    )}

                    {/* Daily report details card */}
                    {(() => {
                      if (viewMode === "attendance") return null;
                      
                      const report = (emp.reports || []).find(r => r.date === today) || emp.reports?.[0];
                      if (!report && filter !== "Daily Report") return null;

                      return (
                        <div style={{
                          marginTop: 10, background: "#F8FAFC", borderRadius: 10, padding: 12,
                          border: `1px solid ${report ? `${TOKENS.gold}66` : TOKENS.border}`,
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 4 }}>
                            <div style={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.navyDeep, display: "flex", alignItems: "center", gap: 5 }}>
                              <FileText size={13} color={TOKENS.gold} />
                              Daily Work Report ({report?.date || today}):
                            </div>
                            {report ? (
                              <div style={{
                                background: `${TOKENS.gold}22`, border: `1px solid ${TOKENS.gold}66`,
                                borderRadius: 10, padding: "2px 8px", fontSize: 10.5, fontWeight: 700, color: TOKENS.navyDeep,
                                display: "flex", alignItems: "center", gap: 4,
                              }}>
                                <Clock size={11} color={TOKENS.navyDeep} />
                                Time: <b>{report.timeSpent || report.hoursSpent}</b>
                              </div>
                            ) : (
                              <span style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted }}>Pending</span>
                            )}
                          </div>

                          {report ? (
                            <>
                              <div style={{ fontSize: 12, color: TOKENS.navySoft, lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                                {report.work || report.workDescription}
                              </div>
                              {report.remarks && (
                                <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px dashed ${TOKENS.border}`, fontSize: 11, color: TOKENS.muted, fontStyle: "italic" }}>
                                  <b>Remarks:</b> {report.remarks}
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ fontSize: 11, color: TOKENS.muted, fontStyle: "italic" }}>
                              No daily report submitted for today.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <ChevronRight size={16} color={TOKENS.muted} style={{ flexShrink: 0 }} />
                </div>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Assign Task Modal overlay */}
      {isAssignOpen && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(10,21,45,0.45)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, padding: 16,
        }}>
          <form onSubmit={handleAssignTask} style={{
            background: "#fff", borderRadius: 20,
            width: "100%", maxWidth: 440, padding: 24,
            boxShadow: "0 24px 60px rgba(10,25,50,0.22)",
            border: `1.5px solid ${TOKENS.border}`,
          }}>
            <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: "0 0 16px" }}>
              Assign Task to Agent
            </h3>

            {/* Select Employee */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              SELECT FIELD AGENT
            </label>
            <select
              value={selectedEmpId}
              onChange={(e) => setSelectedEmpId(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            >
              {team.map((emp) => (
                <option key={emp.employee_id} value={emp.employee_id}>
                  {emp.name} ({emp.employee_id})
                </option>
              ))}
            </select>

            {/* Task Title */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              TASK TITLE / DESCRIPTION
            </label>
            <input
              placeholder="e.g. Calibrate XRF Spectrometer"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {/* Category Dropdown */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              CATEGORY
            </label>
            <select
              value={taskCategory}
              onChange={(e) => setTaskCategory(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            >
              <option value="Calibration">Calibration</option>
              <option value="Site Visit">Site Visit</option>
              <option value="MoU Pickup">MoU Pickup</option>
              <option value="Client Meeting">Client Meeting</option>
            </select>

            {/* Location */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              TARGET LOCATION
            </label>
            <input
              placeholder="e.g. Western Vault, Mumbai"
              value={taskLocation}
              onChange={(e) => setTaskLocation(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {/* Client Ref */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              CLIENT REFERENCE ID
            </label>
            <input
              value={clientRef}
              onChange={(e) => setClientRef(e.target.value)}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13.5,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {/* Client / Project Name */}
            <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 6 }}>
              CLIENT / PROJECT NAME
            </label>
            <input
              placeholder="Client or Project Name"
              value={taskClientName}
              onChange={(e) => {
                setTaskClientName(e.target.value);
                setTaskProjectName(e.target.value);
              }}
              style={{
                width: "100%", padding: "10px 12px", borderRadius: 10,
                border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                background: TOKENS.cream, outline: "none", color: TOKENS.ink,
                marginBottom: 16,
              }}
            />

            {errorMsg && (
              <div style={{ color: TOKENS.danger, fontSize: 12, fontWeight: 600, marginBottom: 14 }}>
                ⚠️ {errorMsg}
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setIsAssignOpen(false)}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${TOKENS.border}`, background: "#fff",
                  color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: "none", background: TOKENS.navyDeep,
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                {loading ? "Assigning..." : "Assign Task"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Employee Modal overlay */}
      {isAddEmpOpen && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(10,21,45,0.45)",
          backdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 9999, padding: 16,
        }}>
          <form onSubmit={handleAddEmployee} style={{
            background: "#fff", borderRadius: 20,
            width: "100%", maxWidth: 480, maxHeight: "90vh", display: "flex", flexDirection: "column",
            boxShadow: "0 24px 60px rgba(10,25,50,0.22)",
            border: `1.5px solid ${TOKENS.border}`, overflow: "hidden",
          }} className="screen-enter">
            <div style={{ padding: "20px 24px 14px", borderBottom: `1px solid ${TOKENS.border}` }}>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                Add New Employee Profile
              </h3>
              <p style={{ fontSize: 11.5, color: TOKENS.muted, margin: "4px 0 0" }}>
                Fill out the employee's login credentials, personal info, and emergency contact.
              </p>
            </div>

            <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
              {/* SECTION 1: Credentials & Job */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>
                👤 Basic & Login Credentials
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    EMPLOYEE ID / USERNAME *
                  </label>
                  <input
                    placeholder="Enter Employee ID"
                    value={newEmpId}
                    onChange={(e) => setNewEmpId(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    PASSWORD *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. agent123"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    FULL NAME *
                  </label>
                  <input
                    placeholder="e.g. Arjun Mehta"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    JOB ROLE / DESIGNATION *
                  </label>
                  <input
                    placeholder="e.g. Field Agent"
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              {/* SECTION 2: Personal Details */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 12px" }}>
                📋 Personal Details
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    FATHER'S NAME
                  </label>
                  <input
                    placeholder="Father's name"
                    value={newFatherName}
                    onChange={(e) => setNewFatherName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    MOTHER'S NAME
                  </label>
                  <input
                    placeholder="Mother's name"
                    value={newMotherName}
                    onChange={(e) => setNewMotherName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    DATE OF BIRTH
                  </label>
                  <input
                    type="date"
                    value={newDob}
                    onChange={(e) => setNewDob(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    BLOOD GROUP
                  </label>
                  <select
                    value={newBloodGroup}
                    onChange={(e) => setNewBloodGroup(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  >
                    {["A+", "B+", "O+", "AB+", "A-", "B-", "O-", "AB-", "A1+", "A1-", "A1B+", "A1B-"].map(bg => (
                      <option key={bg} value={bg}>{bg}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    PHONE NUMBER
                  </label>
                  <input
                    placeholder="+91 98765 43210"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.5, display: "block", marginBottom: 4 }}>
                    EMAIL ADDRESS
                  </label>
                  <input
                    type="email"
                    placeholder="agent@goldpe.in"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 11px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 13,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              {/* SECTION 3: Emergency Contact */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 12px" }}>
                🚨 Emergency Contact
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    CONTACT NAME
                  </label>
                  <input
                    placeholder="Full Name"
                    value={newEmergencyName}
                    onChange={(e) => setNewEmergencyName(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    RELATION
                  </label>
                  <input
                    placeholder="e.g. Father"
                    value={newEmergencyRelationship}
                    onChange={(e) => setNewEmergencyRelationship(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    PHONE
                  </label>
                  <input
                    placeholder="Contact Phone"
                    value={newEmergencyPhone}
                    onChange={(e) => setNewEmergencyPhone(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              {/* SECTION 4: Organization Details */}
              <div style={{ fontSize: 12, fontWeight: 700, color: TOKENS.gold, textTransform: "uppercase", letterSpacing: 0.5, margin: "12px 0 12px" }}>
                🏢 Organization Details
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    CLIENT NAME
                  </label>
                  <input
                    placeholder="Client Name"
                    value={newClient}
                    onChange={(e) => setNewClient(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    ASSIGNED TEAM
                  </label>
                  <input
                    placeholder="Assigned Team"
                    value={newTeam}
                    onChange={(e) => setNewTeam(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 700, color: TOKENS.muted, letterSpacing: 0.4, display: "block", marginBottom: 4 }}>
                    JOINING DATE
                  </label>
                  <input
                    type="date"
                    value={newJoiningDate}
                    onChange={(e) => setNewJoiningDate(e.target.value)}
                    style={{
                      width: "100%", padding: "9px 8px", borderRadius: 10,
                      border: `1.5px solid ${TOKENS.border}`, fontSize: 12.5,
                      background: TOKENS.cream, outline: "none", color: TOKENS.ink, marginBottom: 12,
                    }}
                  />
                </div>
              </div>

              {errorAdd && (
                <div style={{ color: TOKENS.danger, fontSize: 12, fontWeight: 600, marginTop: 8 }}>
                  ⚠️ {errorAdd}
                </div>
              )}
            </div>

            <div style={{ padding: "14px 24px", borderTop: `1px solid ${TOKENS.border}`, display: "flex", gap: 10, background: "#FAFBFD" }}>
              <button
                type="button"
                onClick={() => setIsAddEmpOpen(false)}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: `1.5px solid ${TOKENS.border}`, background: "#fff",
                  color: TOKENS.ink, fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loadingAdd}
                style={{
                  flex: 1, padding: "10px 14px", borderRadius: 10,
                  border: "none", background: TOKENS.navyDeep,
                  color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer",
                }}
              >
                {loadingAdd ? "Creating..." : "Create Profile"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
